import { AxiosRequestConfig, CancelTokenSource, default as staticAxios } from 'axios'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'

import { getLocaleMap } from '../redux/config'
import { LogActions } from '../redux/logs'
import { RootState } from '../redux/types'
import { createRequestParams } from '../utils/api'
import { mapActionError } from '../utils/helpers'

import { useAppConfig } from './useAppConfig'

interface UseApiConfig {
  cancelPrevious?: boolean
}

export function useApi(hookConfig?: UseApiConfig) {
  const { axios } = useAppConfig()
  const { i18n } = useTranslation()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<any | null>(null)
  const [done, setDone] = useState(false)
  const localeMap = useSelector((state: RootState) => getLocaleMap(state, i18n.language))
  const cancelRef = useRef<CancelTokenSource | null>(null)
  const dispatch = useDispatch()
  const request = useCallback(
    async <Response>(config: AxiosRequestConfig, additionalOptions: { umbracoCall?: boolean } = {}) => {
      const { umbracoCall } = additionalOptions

      if (hookConfig?.cancelPrevious && cancelRef.current) {
        cancelRef.current.cancel()
      }
      const cancel = staticAxios.CancelToken.source()
      cancelRef.current = cancel

      const reqConfig = createRequestParams(config, localeMap, {
        umbracoCall,
        cancelToken: cancel.token
      })

      setLoading(true)
      setDone(false)

      try {
        const response = await axios<Response>(reqConfig)
        setError(null)
        return response
      } catch (e: any) {
        if (!staticAxios.isCancel(e)) {
          dispatch(LogActions.addLogItem(mapActionError(e, 'ERROR', location.pathname)))
        }
        setError(e)
        throw e
      } finally {
        setLoading(false)
        setDone(true)
      }
    },
    [axios, hookConfig?.cancelPrevious, localeMap, dispatch]
  )

  useEffect(() => {
    return () => cancelRef.current?.cancel()
  }, [])

  return {
    request,
    loading,
    done,
    error,
    cancel: () => cancelRef.current?.cancel()
  }
}
